<script setup lang="ts">
import DeleteEntityDialog from '@/common/components/DeleteEntityDialog.vue';
import ColumnSelectorButton from 'shared/common/components/ColumnSelectorButton.vue';
import { useColumnSelector, type ColumnField as SharedColumnField, type ColumnFilterType } from 'shared/common/composables/useColumnSelector';
import { useColumnFilters } from 'shared/common/composables/useColumnFilters';
import ColumnFilterCell from 'shared/common/components/ColumnFilterCell.vue';
import { Button, Card, Column, DataTable, InputText, Menu, type DataTableRowDoubleClickEvent } from 'primevue';
import type { MenuItem } from 'primevue/menuitem';
import AuditDialog from 'shared/audit/components/AuditDialog.vue';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import { usePackageAuditApi } from '@/audit/api/packageAuditApi';
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { clearCorruptDataTableState } from 'shared/common/dataTableState';
import { useDataTableMultiSort } from 'shared/common/composables/useDataTableMultiSort';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { formatMeasurement } from 'shared/common/domain/units';
import { formatWeight } from 'shared/common/domain/weightUnits';
import { useFieldUnit, useWeightUnit } from 'shared/common/composables/fieldUnit';
import { storeToRefs } from 'pinia';
import { usePackageStore } from 'shared/packages/stores/packageStore';
import { usePackageTypeStore } from 'shared/packages/stores/packageTypeStore';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { computePackageAccessibilityRatings } from 'shared/packages/domain/packageAccessibility';
import { useRefreshPackageStore } from '../commands/refreshPackageStore';
import { useRefreshPackageTypeStore } from '../commands/refreshPackageTypeStore';
import { useRefreshStorageAreaStore } from '@/storageAreas/stores/refreshStorageAreaStore';
import EditPackageDialog from './EditPackageDialog.vue';
import { useDeletePackage } from '../api/deletePackage';

const selectedColumnsLocalStorageKey = 'packages-table-columns';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const pageTranslations = computed(() => translations.value.packages);

const packageStore = usePackageStore();
const packageTypeStore = usePackageTypeStore();
const storageAreaStore = useStorageAreaStore();
const refreshPackageStore = useRefreshPackageStore();
const refreshPackageTypeStore = useRefreshPackageTypeStore();
const refreshStorageAreaStore = useRefreshStorageAreaStore();
const deletePackage = useDeletePackage();
const packageAuditApi = usePackageAuditApi();
const actionFailedAlert = useActionFailedAlert();

const selectedPackages = ref<{ id: string }[]>([]);
const menuPackageId = ref<string>();
const editDialog = useTemplateRef('edit-dialog');
const deleteDialog = useTemplateRef('delete-dialog');
const packageMenu = useTemplateRef('package-menu');
const auditDialog = useTemplateRef('audit-dialog');

type ColumnFormat =
  | 'text'
  | 'date'
  | 'weight'
  | 'volume'
  | 'runningMeters'
  | 'measurement'
  | 'integer'
  | 'coordinate'
  | 'rotation'
  | 'distanceKm'
  | 'duration';

interface ColumnField extends SharedColumnField {
  format?: ColumnFormat;
  /** Shared unit-preference key for measurement/weight columns. */
  unitKey?: string;
}

const baseColumns = computed<ColumnField[]>(() => [
  { field: 'number', header: pageTranslations.value.fields.number, format: 'text' },
  { field: 'externalNumber', header: pageTranslations.value.fields.externalNumber, format: 'text' },
  { field: 'status', header: translations.value.generic.status, format: 'text' },
  { field: 'statusValidFrom', header: pageTranslations.value.fields.statusValidFrom, format: 'date' },
  { field: 'statusValidTo', header: pageTranslations.value.fields.statusValidTo, format: 'date' },
  { field: 'packageTypeName', header: translations.value.packageTypes.packageTypes, format: 'text' },
  {
    field: 'composition',
    header: pageTranslations.value.fields.composition,
    format: 'text',
    filterType: 'select',
    filterOptions: Object.values(pageTranslations.value.compositions).map((label) => ({ label, value: label })),
  },
  { field: 'piecesCount', header: pageTranslations.value.fields.piecesCount, format: 'integer' },
  { field: 'layers', header: pageTranslations.value.fields.layers, format: 'integer' },
  { field: 'piecesPerLayer', header: pageTranslations.value.fields.piecesPerLayer, format: 'integer' },
  { field: 'spacerHeight', header: pageTranslations.value.fields.spacerHeight, format: 'measurement', unitKey: 'package-spacer-height' },
  { field: 'packageStorageAreaName', header: pageTranslations.value.fields.packageStorageArea, format: 'text' },
  { field: 'occupiedStoragePlaces', header: pageTranslations.value.fields.occupiedStoragePlaces, format: 'text' },
  { field: 'stackLevel', header: pageTranslations.value.fields.stackLevel, format: 'integer' },
  { field: 'accessibilityRating', header: pageTranslations.value.fields.accessibilityRating, format: 'integer' },
  { field: 'length', header: pageTranslations.value.fields.length, format: 'measurement', unitKey: 'package-length' },
  { field: 'nominalLength', header: pageTranslations.value.fields.nominalLength, format: 'measurement', unitKey: 'package-nominal-length' },
  { field: 'width', header: pageTranslations.value.fields.width, format: 'measurement', unitKey: 'package-width' },
  { field: 'nominalWidth', header: pageTranslations.value.fields.nominalWidth, format: 'measurement', unitKey: 'package-nominal-width' },
  { field: 'height', header: pageTranslations.value.fields.height, format: 'measurement', unitKey: 'package-height' },
  { field: 'nominalHeight', header: pageTranslations.value.fields.nominalHeight, format: 'measurement', unitKey: 'package-nominal-height' },
  { field: 'volume', header: pageTranslations.value.fields.volume, format: 'volume' },
  { field: 'nominalVolume', header: pageTranslations.value.fields.nominalVolume, format: 'volume' },
  { field: 'weight', header: pageTranslations.value.fields.weight, format: 'weight', unitKey: 'package-weight' },
  { field: 'nominalWeight', header: pageTranslations.value.fields.nominalWeight, format: 'weight', unitKey: 'package-nominal-weight' },
  { field: 'runningMeters', header: pageTranslations.value.fields.runningMeters, format: 'runningMeters' },
  { field: 'nominalRunningMeters', header: pageTranslations.value.fields.nominalRunningMeters, format: 'runningMeters' },
  { field: 'longitude', header: translations.value.generic.longitude, format: 'coordinate' },
  { field: 'latitude', header: translations.value.generic.latitude, format: 'coordinate' },
  { field: 'rotation', header: translations.value.generic.rotation, format: 'rotation' },
  { field: 'comment', header: pageTranslations.value.fields.comment, format: 'text' },
  { field: 'timesMovedCount', header: pageTranslations.value.fields.timesMovedCount, format: 'integer' },
  { field: 'distanceTravelledKm', header: pageTranslations.value.fields.distanceTravelledKm, format: 'distanceKm' },
  { field: 'timeCarried', header: pageTranslations.value.fields.timeCarried, format: 'duration' },
  { field: 'weightedDistanceTravelledKm', header: pageTranslations.value.fields.weightedDistanceTravelledKm, format: 'distanceKm' },
  { field: 'createdAt', header: pageTranslations.value.fields.createdAt, format: 'date' },
]);

const filterTypeForFormat = (format?: ColumnFormat): ColumnFilterType | undefined => {
  if (!format) return undefined;
  return format === 'text' || format === 'date' ? 'text' : 'numeric';
};

const columns = computed<ColumnField[]>(() =>
  baseColumns.value.map((col) => ({ ...col, filterType: col.filterType ?? filterTypeForFormat(col.format) })),
);

const defaultFields = [
  'number',
  'externalNumber',
  'status',
  'packageTypeName',
  'packageStorageAreaName',
  'accessibilityRating',
  'length',
  'volume',
  'weight',
  'comment',
];

const dimensionUnits: Record<string, ReturnType<typeof useFieldUnit>> = {
  'package-length': useFieldUnit('package-length'),
  'package-nominal-length': useFieldUnit('package-nominal-length'),
  'package-width': useFieldUnit('package-width'),
  'package-nominal-width': useFieldUnit('package-nominal-width'),
  'package-height': useFieldUnit('package-height'),
  'package-nominal-height': useFieldUnit('package-nominal-height'),
  'package-spacer-height': useFieldUnit('package-spacer-height'),
};

const weightUnits: Record<string, ReturnType<typeof useWeightUnit>> = {
  'package-weight': useWeightUnit('package-weight'),
  'package-nominal-weight': useWeightUnit('package-nominal-weight'),
};

function formatCell(col: ColumnField, value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  switch (col.format) {
    case 'date':
      return languageStore.formatDateTime(value as string);
    case 'weight':
      return formatWeight(value as number, weightUnits[col.unitKey!]!.value);
    case 'volume':
      return `${languageStore.formatNumber(value as number, 3)} ${translations.value.units.cubicMeter}`;
    case 'runningMeters':
      return `${languageStore.formatNumber(value as number, 3)} m`;
    case 'measurement':
      return formatMeasurement(value as number, dimensionUnits[col.unitKey!]!.value);
    case 'integer':
      return languageStore.formatNumber(value as number, 0);
    case 'coordinate':
      return `${languageStore.formatNumber(value as number, 6)}°`;
    case 'rotation':
      return `${languageStore.formatNumber(value as number, 1)}°`;
    case 'distanceKm':
      return `${languageStore.formatNumber(value as number, 3)} km`;
    case 'duration': {
      const secs = value as number;
      const h = Math.floor(secs / 3600);
      const m = Math.floor((secs % 3600) / 60);
      const s = secs % 60;
      return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    default:
      return String(value);
  }
}

const { orderedColumns, selectedColumns, setSelectedColumns, setOrder, reset: resetColumns } =
  useColumnSelector({
    storageKey: selectedColumnsLocalStorageKey,
    columns,
    defaultFields,
  });

function occupiedPlacesLabel(item: { packageStorageAreaId: string | null; occupiedStoragePlaceIds: string[] }): string | null {
  if (!item.packageStorageAreaId || item.occupiedStoragePlaceIds.length === 0) return null;
  const area = storageAreaStore.getById(item.packageStorageAreaId);
  const places = area?.packageStorageData?.places ?? area?.packagePlaceData?.places ?? [];
  const ids = new Set(item.occupiedStoragePlaceIds);
  const label = places
    .filter((place) => ids.has(place.id))
    .map((place) => place.displayText)
    .join(', ');
  return label || null;
}

const rows = computed(() => {
  const areaCandidates = packageStore.packages
    .map((item) => (item.packageStorageAreaId ? storageAreaStore.getById(item.packageStorageAreaId) : null))
    .filter((area): area is NonNullable<typeof area> => !!area);
  const areas = [...new Map(areaCandidates.map((area) => [area.id, area])).values()];
  const ratings = computePackageAccessibilityRatings(packageStore.packages, areas);

  return packageStore.sortedByNumber.map((item) => ({
    id: item.id,
    number: item.number,
    externalNumber: item.externalNumber,
    status: item.status,
    statusValidFrom: item.statusValidFrom,
    statusValidTo: item.statusValidTo,
    packageTypeName: item.packageTypeId ? (packageTypeStore.getById(item.packageTypeId)?.name ?? null) : null,
    composition: pageTranslations.value.compositions[item.composition] ?? item.composition,
    piecesCount: item.piecesCount,
    layers: item.layers,
    piecesPerLayer: item.piecesPerLayer,
    spacerHeight: item.spacerHeight,
    packageStorageAreaName: item.packageStorageAreaId ? (storageAreaStore.getById(item.packageStorageAreaId)?.name ?? null) : null,
    occupiedStoragePlaces: occupiedPlacesLabel(item),
    stackLevel: item.stackLevel,
    accessibilityRating: ratings.get(item.id)?.score ?? 100,
    length: item.length,
    nominalLength: item.nominalLength,
    width: item.width,
    nominalWidth: item.nominalWidth,
    height: item.height,
    nominalHeight: item.nominalHeight,
    volume: item.volume,
    nominalVolume: item.nominalVolume,
    weight: item.weight,
    nominalWeight: item.nominalWeight,
    runningMeters: item.runningMeters,
    nominalRunningMeters: item.nominalRunningMeters,
    longitude: item.longitude,
    latitude: item.latitude,
    rotation: item.rotation,
    comment: item.comment,
    timesMovedCount: item.timesMovedCount,
    distanceTravelledKm: item.distanceTravelledMetres / 1000,
    timeCarried: item.timeCarriedSeconds,
    weightedDistanceTravelledKm: item.weightedDistanceTravelledMetres / 1000,
    createdAt: item.createdAtUtc,
  }));
});

const { filters, filteredCount, hasActiveFilters, clearFilters, onFilter } = useColumnFilters({
  columns,
  totalCount: () => rows.value.length,
});

// Clear corrupt persisted sort state before the DataTable restores it while rendering.
clearCorruptDataTableState('packages-table-state');

onMounted(() => {
  refreshStorageAreaStore.initialize();
  refreshPackageTypeStore.initialize();
  refreshPackageStore.initialize();
});

const { multiSortMeta, sortBadgeClass } = useDataTableMultiSort();

function onAdd() {
  editDialog.value?.show();
}

function onRowDoubleClick(ev: DataTableRowDoubleClickEvent) {
  const row = ev.data as { id: string };
  editDialog.value?.show(row.id);
}

const packageMenuItems = computed<MenuItem[]>(() => [
  {
    label: translations.value.generic.edit,
    icon: 'pi pi-pencil',
    command() {
      const id = menuPackageId.value;
      if (!id) return;
      editDialog.value?.show(id);
    },
  },
  {
    label: translations.value.generic.copy,
    icon: 'pi pi-copy',
    command() {
      const id = menuPackageId.value;
      if (!id) return;
      editDialog.value?.showCopy(id);
    },
  },
  {
    label: translations.value.generic.delete,
    icon: 'pi pi-trash',
    command() {
      const id = menuPackageId.value;
      if (!id) return;
      deleteDialog.value?.show(id);
    },
  },
  {
    label: translations.value.generic.audit,
    icon: 'pi pi-search',
    async command() {
      const id = menuPackageId.value;
      if (!id) return;

      const response = await packageAuditApi.execute({ id });

      if (!response.ok) {
        actionFailedAlert.show(response.details);
        return;
      }

      auditDialog.value?.show({ audit: response.audit });
    },
  },
]);

function togglePackageMenu(ev: Event, id: string) {
  menuPackageId.value = id;
  packageMenu.value?.toggle(ev);
}

function onDeleted(id: string) {
  selectedPackages.value = selectedPackages.value.filter((pkg) => pkg.id !== id);
}

function getPackageForDelete(id: string) {
  return Promise.resolve({
    id,
    name: packageStore.getById(id)?.number ?? id,
  });
}
</script>

<template>
  <Card>
    <template #title>
      <div class="title-row">
        <span>{{ pageTranslations.packages }}</span>
        <div class="actions">
          <span v-if="hasActiveFilters && filteredCount < rows.length" class="filtered-count">
            ({{ translations.dataTable.showing }} {{ filteredCount }} {{ translations.dataTable.of }} {{ rows.length }})
          </span>
          <Button v-if="hasActiveFilters" type="button" severity="secondary" size="small" icon="pi pi-filter-slash"
            :label="translations.dataTable.clearFilters" @click="clearFilters" />
          <Button :label="translations.generic.add" @click="onAdd"></Button>
          <InputText v-model="filters.global.value" :placeholder="translations.generic.search"></InputText>
        </div>
      </div>
    </template>
    <template #content>
      <DataTable
        v-model:selection="selectedPackages"
        v-model:filters="filters"
        v-model:multiSortMeta="multiSortMeta"
        :class="sortBadgeClass"
        :value="rows"
        data-key="id"
        selectionMode="multiple"
        meta-key-selection
        sort-mode="multiple"
        stateStorage="local"
        stateKey="packages-table-state"
        filterDisplay="menu"
        @filter="onFilter"
        @row-dblclick="onRowDoubleClick"
      >
        <Column v-for="(col, index) of selectedColumns" :key="col.field + '_' + index" :field="col.field" :header="col.header" sortable
          :filter="!!col.filterType" :filterField="col.filterField ?? col.field"
          :showFilterMatchModes="col.showFilterMatchModes" :showFilterOperator="col.showFilterOperator"
          :dataType="col.filterType === 'numeric' ? 'numeric' : undefined">
          <template #body="{ data }">{{ formatCell(col, data[col.field]) }}</template>
          <template v-if="col.filterType" #filter="{ filterModel, filterCallback }">
            <ColumnFilterCell :filter-type="col.filterType" :filter-model="filterModel" :filter-callback="filterCallback"
              :options="col.filterOptions"
              :placeholder="translations.dataTable.filterBy + ' ' + col.header.toLocaleLowerCase()" />
          </template>
        </Column>
        <Column column-key="column-selector" class="w-12 !text-end">
          <template #header>
            <ColumnSelectorButton :columns="orderedColumns" :modelValue="selectedColumns"
              @update:modelValue="setSelectedColumns" @reorder="setOrder" @reset="resetColumns" />
          </template>
          <template #body="{ data }">
            <Button icon="pi pi-ellipsis-v" variant="text" severity="secondary" rounded
              @click="(ev) => togglePackageMenu(ev, data.id)"></Button>
          </template>
        </Column>
      </DataTable>
    </template>
  </Card>

  <EditPackageDialog ref="edit-dialog"></EditPackageDialog>
  <DeleteEntityDialog
    ref="delete-dialog"
    :delete="(id) => deletePackage.execute({ id })"
    :getById="getPackageForDelete"
    :translate="(t) => t.packages.deleteDialog"
    @deleted="onDeleted"
  ></DeleteEntityDialog>
  <AuditDialog ref="audit-dialog"></AuditDialog>
  <Menu ref="package-menu" :model="packageMenuItems" :popup="true" />
</template>

<style scoped>
.title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0rem;
}

.actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
}

.filtered-count {
  font-weight: 400;
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}
</style>
